<template>
	<div class="fd-month-selector">
		<button type="button" class="fd-mini-pager-button" :disabled="disableLeft" @click="goPrev">
			<sym-icon name="caret-left-14-bold"></sym-icon>
		</button>
		<select v-model="selectedMonth">
			<option v-for="m in selectableMonths" :value="m.value" :key="m.value">{{ m.name }}</option>
		</select>
		<button type="button" class="fd-mini-pager-button" :disabled="disableRight" @click="goNext">
			<sym-icon name="caret-right-14-bold"></sym-icon>
		</button>
	</div>
</template>

<script setup lang="ts">
import SymIcon from "../../helpers/SymIcon.vue";
import {computed, watch} from "vue";

const props = defineProps({
	year: Number,
	month: Number,
	disableLeft: Boolean,
	disableRight: Boolean
});
const emit = defineEmits(['update:year', 'update:month']);

export interface ParsedYM {
	year: number,
	month: number
}

export interface ParsedYMN {
	year: number,
	month: number,
	name: string,
	value: string
}

const current = computed<ParsedYM>(() => {
	if (props.year == null || props.month == null) {
		const today = new Date();
		return {
			year: today.getFullYear(),
			month: today.getMonth()
		}
	}
	return normalize({
		year: props.year,
		month: props.month
	});
});

const selectedMonth = computed<string>({
	get(): string {
		return format(current.value.year, current.value.month);
	},
	set(v: string): void {
		const n = parse(v);
		setYearMonth(n);
	}
});

function setYearMonth(ym: ParsedYM | null): void {
	if (ym == null) {
		emit('update:year', null);
		emit('update:month', null);
	} else {
		if (ym.year !== props.year) {
			emit('update:year', ym.year);
		}
		if (ym.month !== props.month) {
			emit('update:month', ym.month);
		}
	}
}

const selectableMonths = computed<ParsedYMN[]>(() => {
	const mns: ParsedYMN[] = [];
	const c = current.value;
	for (let mshift = -12; mshift < 20; mshift++) {
		const s = shift(c, mshift);
		mns.push({
			...s,
			name: monthName(s.year, s.month),
			value: format(s.year, s.month)
		});
	}
	return mns;
});

function parse(d: string | null | undefined): ParsedYM | null {
	if (d == null) return null;
	const dash = d.indexOf("-");
	if (dash > 0) {
		return {
			year: parseInt(d.substring(0, dash)),
			month: parseInt(d.substring(dash + 1)) - 1
		};
	}
	return null;
}

function formatNullable(year: number | null | undefined, month: number | null | undefined): string | null {
	if (year == null || month == null) return null; // incomplete
	return format(year, month);
}


function format(year: number, month: number): string {
	return year + "-" + (month + 1);
}

const MONTH_FORMAT = new Intl.DateTimeFormat(undefined, {
	month: "long",
});

/** Human readable month name */
function monthName(year: number, month: number): string {
	const d = new Date();
	d.setFullYear(year, month, 1);
	return MONTH_FORMAT.format(d) + " " + year;
}


function shift(parsed: ParsedYM, months: number) {
	let y = parsed.year;
	let m = parsed.month + months;
	if (m >= 12) {
		y += Math.floor(m / 12);
		m = m % 12;
	} else if (m < 0) {
		const yshift = Math.ceil(m / -12);
		y -= yshift;
		m = Math.abs(m + 12 * yshift) % 12;
	}
	return {
		year: y,
		month: m,
	};
}

function normalize(n: ParsedYM) {
	if (n.month >= 0 && n.month < 12) return n;
	return shift({year: n.year, month: 0}, n.month);
}

function goPrev() {
	const c = current.value;
	setYearMonth(shift(c, -1));
}

function goNext() {
	const c = current.value;
	setYearMonth(shift(c, 1));
}
</script>

<style>
.fd-mini-pager-button {
	background-color: transparent;
	padding: 0;
	margin: 0;
	border: none;
	min-width: 24px;
	color: var(--cal-fg);

	&:hover {
		color: var(--cal-fg-hover);
	}
}

.fd-mini-pager-button:disabled {
	color: var(--inactive);
	cursor: not-allowed;
}

.fd-month-selector {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	margin-top:4px;
	margin-bottom:4px;
	background-color: var(--bg);
	color: var(--fg);
}
</style>